import React from "react";
import { CheckCircle2, Phone, Calendar, ShieldCheck, Sparkles } from "lucide-react";
import { useLang } from "@/App";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/nq85lmpb_esta%202.JPG";

export default function Hero() {
  const { t } = useLang();

  return (
    <section
      id="top"
      className="pt-40 md:pt-44 pb-16 md:pb-24 hero-pattern relative overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-900 text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            {t.hero.badge}
          </div>

          <h1 className="font-extrabold tracking-tight text-blue-950 leading-[1.05] text-5xl md:text-6xl lg:text-7xl">
            {t.hero.title1}{" "}
            <span className="text-sky-600">{t.hero.title2}</span>
          </h1>

          <p className="mt-7 text-lg md:text-xl text-slate-700 max-w-2xl leading-relaxed">
            {t.hero.subtitle}
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button
              asChild
              className="h-14 px-7 bg-blue-900 hover:bg-blue-800 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="hero-primary-cta"
            >
              <a href="#contacto" className="inline-flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {t.hero.ctaPrimary}
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-14 px-7 border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white text-lg font-semibold rounded-xl"
              data-testid="hero-secondary-cta"
            >
              <a href="tel:+34965637618" className="inline-flex items-center gap-2">
                <Phone className="w-5 h-5" />
                {t.hero.ctaSecondary}
              </a>
            </Button>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {t.hero.bullets.map((b, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`hero-bullet-${i}`}>
                <CheckCircle2 className="w-6 h-6 text-sky-600 shrink-0 mt-0.5" />
                <span className="text-base text-slate-800 font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 fade-up delay-200">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-sky-300/30 to-blue-700/20 rounded-3xl blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
              <img
                src={HERO_IMG}
                alt="Audióloga atendiendo a un paciente en Amplisoon"
                className="w-full h-[480px] md:h-[560px] object-cover"
                data-testid="hero-image"
              />
            </div>

            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 max-w-[260px]">
              <div className="flex items-center gap-3">
                <div className="grid place-items-center w-12 h-12 rounded-xl bg-emerald-100 text-emerald-700">
                  <ShieldCheck className="w-7 h-7" />
                </div>
                <div>
                  <div className="text-lg font-extrabold text-slate-900 leading-tight">30 días</div>
                  <div className="text-sm text-slate-600 leading-tight">
                    {t.hero.bullets[1]}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
